import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, X, Video, Image as ImageIcon } from "lucide-react";
import { api, formatError, fileUrl } from "@/lib/api";
import { Page } from "@/components/Page";

async function uploadFile(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return { path: r.data.path, kind: r.data.kind || kind };
}

export function AdminHomeAd() {
  const [f, setF] = useState({ enabled: false, headline: "", subtext: "", media_path: "", media_kind: "image", link_url: "", link_label: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/home-ad").then(r => setF({
    enabled: !!r.data.enabled,
    headline: r.data.headline || "",
    subtext: r.data.subtext || "",
    media_path: r.data.media_path || "",
    media_kind: r.data.media_kind || "image",
    link_url: r.data.link_url || "",
    link_label: r.data.link_label || "",
  })).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const kind = file.type.startsWith("video/") ? "video" : "image";
      const r = await uploadFile(file, kind);
      setF(x => ({ ...x, media_path: r.path, media_kind: r.kind }));
      toast.success(`${kind === "video" ? "Video" : "Image"} uploaded`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const clearMedia = () => setF(x => ({ ...x, media_path: "" }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/admin/home-ad", f); toast.success("Home ad updated"); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <Page title="Home page advertisement" subtitle="A single promo shown below the hero on the storefront. Use it for offers, events or announcements.">
      <form className="form-section" onSubmit={save}>
        <label className="check-line"><input type="checkbox" checked={f.enabled} onChange={e => setF({ ...f, enabled: e.target.checked })} data-testid="home-ad-enabled" /><span>Show this ad on the homepage</span></label>
        <label>Headline<input required value={f.headline} onChange={e => setF({ ...f, headline: e.target.value })} data-testid="home-ad-headline" placeholder="e.g., Independence Day offer — 15% off" /></label>
        <label>Subtext<textarea rows={3} value={f.subtext} onChange={e => setF({ ...f, subtext: e.target.value })} data-testid="home-ad-subtext" placeholder="One or two lines explaining the offer / event" /></label>

        <div className="field-grid">
          <label>Link URL (optional)<input value={f.link_url} onChange={e => setF({ ...f, link_url: e.target.value })} data-testid="home-ad-link-url" placeholder="https:// or /internal-path" /></label>
          <label>Link label<input value={f.link_label} onChange={e => setF({ ...f, link_label: e.target.value })} data-testid="home-ad-link-label" placeholder="e.g., Shop the offer" /></label>
        </div>

        <div className="uploader" data-testid="home-ad-uploader">
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <label className="upload-btn" data-testid="home-ad-upload-label">
              {f.media_kind === "video" ? <Video size={14} /> : <ImageIcon size={14} />}
              {uploading ? "Uploading…" : "Upload image or video"}
              <input type="file" accept="image/*,video/*" onChange={onUpload} data-testid="home-ad-file-input" />
            </label>
            <span className="muted">Accepts JPG/PNG/WebP images (≤8MB) or MP4/MOV/WebM videos (≤40MB).</span>
          </div>
          {f.media_path && (
            <div className="upload-grid" style={{ marginTop: 12 }}>
              <div className="upload-thumb" data-testid="home-ad-preview">
                {f.media_kind === "video"
                  ? <video src={fileUrl(f.media_path)} controls muted />
                  : <img src={fileUrl(f.media_path)} alt="Home ad" />}
                <button type="button" className="remove" onClick={clearMedia} data-testid="home-ad-remove-media" aria-label="Remove media"><X size={12} /></button>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <span className="muted">The homepage picks this up instantly.</span>
          <button className="primary-btn" disabled={busy || uploading} data-testid="home-ad-save">{busy ? "Saving…" : <>Save ad <Save size={14} /></>}</button>
        </div>
      </form>
    </Page>
  );
}
